import type {
  AutoConfiguratorState,
  Field,
  FieldError,
  FieldId,
  Value,
  ValueId,
} from '@snap-types/models/auto-configurator/state';
import { Decimal } from '@snap-types/types/decimal';
import { z } from 'zod';

import { getField } from '../../logic/field';
import { getValue } from '../../logic/value';

interface ValidateFieldValueParams {
  state: AutoConfiguratorState;
  fieldId: FieldId;
  valueId: ValueId;
  field: Field;
  value: Value;
  includeCustomValidators?: boolean;
}

export function validateFieldValue({
  state,
  fieldId,
  valueId,
  field,
  value,
  includeCustomValidators = true,
}: ValidateFieldValueParams): FieldError[] {
  const builtInErrors = validateBuiltInRules(field, value);

  if (!includeCustomValidators) {
    return builtInErrors;
  }

  const customErrors = (field.definition.validators ?? []).flatMap(
    (validator) => {
      return (
        validator({
          state,
          fieldId,
          valueId,
          field,
          value,
          getField: (id) => getField(state, id),
          getValue: (id) => getValue(state, id),
        }) ?? []
      );
    },
  );

  return [...builtInErrors, ...customErrors];
}

function validateBuiltInRules(field: Field, value: Value): FieldError[] {
  const definition = field.definition;

  const errors: FieldError[] = [];

  if (definition.isRequired && isEmptyValue(value)) {
    errors.push({ message: 'Required' });
  }

  switch (value.type) {
    case 'decimal': {
      return [...errors, ...validateDecimalRules(field, definition, value)];
    }

    case 'number': {
      return [...errors, ...validateNumberRules(field, definition, value)];
    }

    case 'select': {
      return [...errors, ...validateSelectRules(field, definition, value)];
    }

    case 'text': {
      return [...errors, ...validateTextRules(field, definition, value)];
    }

    case 'description': {
      return [...errors, ...validateDescriptionRules(field, definition, value)];
    }

    default:
      return errors;
  }
}

function validateDecimalRules(
  field: Field,
  definition: Field['definition'],
  value: Extract<Value, { type: 'decimal' }>,
): FieldError[] {
  if (definition.type !== 'decimal') {
    return buildTypeMismatchErrors(field, definition.type, value.type);
  }

  if (value.value === null) {
    return [];
  }

  const schema = buildDecimalSchema(definition.validation);
  const result = schema.safeParse(value.value);

  if (!result.success) {
    return toFieldErrors(result.error.issues);
  }

  return [];
}

function validateNumberRules(
  field: Field,
  definition: Field['definition'],
  value: Extract<Value, { type: 'number' }>,
): FieldError[] {
  if (definition.type !== 'number') {
    return buildTypeMismatchErrors(field, definition.type, value.type);
  }

  if (value.value === null) {
    return [];
  }

  const schema = buildNumberSchema(definition.validation);
  const result = schema.safeParse(value.value);

  if (!result.success) {
    return toFieldErrors(result.error.issues);
  }

  return [];
}

function validateSelectRules(
  field: Field,
  definition: Field['definition'],
  value: Extract<Value, { type: 'select' }>,
): FieldError[] {
  if (definition.type !== 'select') {
    return buildTypeMismatchErrors(field, definition.type, value.type);
  }

  if (value.value === null) {
    return [];
  }

  return validateSelectValue(definition.validation, value.value.value);
}

function validateTextRules(
  field: Field,
  definition: Field['definition'],
  value: Extract<Value, { type: 'text' }>,
): FieldError[] {
  if (definition.type !== 'text') {
    return buildTypeMismatchErrors(field, definition.type, value.type);
  }

  if (value.value === null) {
    return [];
  }

  const schema = buildTextSchema(definition.validation);
  const result = schema.safeParse(value.value);

  if (!result.success) {
    return toFieldErrors(result.error.issues);
  }

  return [];
}

function validateDescriptionRules(
  field: Field,
  definition: Field['definition'],
  value: Extract<Value, { type: 'description' }>,
): FieldError[] {
  if (definition.type !== 'description') {
    return buildTypeMismatchErrors(field, definition.type, value.type);
  }

  if (value.value === null) {
    return [];
  }

  const schema = buildTextSchema(definition.validation);
  const result = schema.safeParse(value.value);

  if (!result.success) {
    return toFieldErrors(result.error.issues);
  }

  return [];
}

function buildTypeMismatchErrors(
  field: Field,
  expectedType: Field['definition']['type'],
  receivedType: Value['type'],
): FieldError[] {
  return [
    {
      message: `Value type mismatch for field "${field.model}.${field.key}"`,
      context: {
        expectedType,
        receivedType,
      },
    },
  ];
}

function buildDecimalSchema(
  validation: Extract<Field['definition'], { type: 'decimal' }>['validation'],
) {
  let schema = z.custom<Decimal>((input) => Decimal.isDecimal(input), {
    message: 'Not a valid decimal number',
  });

  if (validation?.min !== undefined) {
    const min = validation.min;

    schema = schema.refine((input) => input.greaterThanOrEqualTo(min), {
      message: `Must be greater than or equal to ${min}`,
    });
  }

  if (validation?.max !== undefined) {
    const max = validation.max;

    schema = schema.refine((input) => input.lessThanOrEqualTo(max), {
      message: `Must be less than or equal to ${max}`,
    });
  }

  return schema;
}

function buildNumberSchema(
  validation: Extract<Field['definition'], { type: 'number' }>['validation'],
) {
  let schema = z.number();

  if (validation?.min !== undefined) {
    schema = schema.min(validation.min);
  }

  if (validation?.max !== undefined) {
    schema = schema.max(validation.max);
  }

  return schema;
}

function buildTextSchema(
  validation: Extract<Field['definition'], { type: 'text' }>['validation'],
) {
  let schema = z.string();

  if (validation?.minLength !== undefined) {
    schema = schema.min(validation.minLength);
  }

  if (validation?.maxLength !== undefined) {
    schema = schema.max(validation.maxLength);
  }

  return schema;
}

function validateSelectValue(
  validation: Extract<Field['definition'], { type: 'select' }>['validation'],
  selectedValue: string,
): FieldError[] {
  const allowedValues = validation?.allowedValues;

  if (!allowedValues || allowedValues.includes(selectedValue)) {
    return [];
  }

  return [
    {
      message: 'Not an allowed option',
      context: {
        allowedValues,
        receivedValue: selectedValue,
      },
    },
  ];
}

function isEmptyValue(value: Value): boolean {
  switch (value.type) {
    case 'decimal':
    case 'number':
      return value.rawValue.trim() === '';

    case 'select':
      return value.rawValue.trim() === '';

    case 'text':
    case 'description':
      return value.rawValue.trim() === '';

    default:
      return true;
  }
}

function toFieldErrors(issues: z.ZodIssue[]): FieldError[] {
  return issues.map((issue) => ({
    message: issue.message,
    context: {
      code: issue.code,
      path: issue.path,
    },
  }));
}
